import React, { FC } from 'react';
import { isEmpty, includes, size } from 'lodash';
import PropTypes from 'prop-types';
import { Button } from '@src/components/Button';
import { FUNNEL_MESSAGE_ROUTE } from '@src/Routes/links';
import { idify } from '@src/util/idify';
import { EMPLOYMENT_TYPES } from '@src/constants';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { isSalaryRangeComplete } from '../util/creationSteps';
import SalaryBudget from './Compensation/SalaryBudget';
import Specialities from './Specialities';
import Skills from './Skills';
import Seniorities from './Seniorities';
import { Locations } from './Locations';
import './styles.scss';

export const TOP_LEVEL_MANAGEMENT_KEY = 'top_level_management';

export const View: FC = ({
  opening: providedOpening,
  setSpeciality,
  setField,
  setSeniorities,
  setSkills,
  updatedAt,
  saving,
}) => {
  const opening = {
    ...providedOpening,
    // "Other" is a fallback when scraper can't predict an opening speciality, which causes skills request to break
    // we handle it as null and let recruiter select speciality and skill
    speciality: providedOpening?.speciality?.key === 'other' ? null : providedOpening?.speciality,
  };

  const { employmentType, salaryRange } = opening;
  const openingId = idify(opening);

  const isTopLevelManagementOpening = opening.speciality && opening.speciality.key === TOP_LEVEL_MANAGEMENT_KEY;
  const skipSeniority =
    isTopLevelManagementOpening ||
    includes([EMPLOYMENT_TYPES.PROJECT.key, EMPLOYMENT_TYPES.INTERNSHIP.key], opening.employmentType);

  const isSkillsDisplayed = !!(skipSeniority || size(opening.seniorities)) && !!opening.speciality;

  const isLocationsDisplayed = !isEmpty(opening.skills) && !!opening.speciality;

  return (
    <div className="create-opening-form-content">
      <div className="page-section">
        <Specialities activeSpeciality={opening.speciality} setSpeciality={setSpeciality} />

        {!skipSeniority && <Seniorities activeSeniorities={opening.seniorities} setSeniorities={setSeniorities} />}

        {isSkillsDisplayed && (
          <Skills
            activeSkills={opening.skills}
            setSkills={setSkills}
            specialityId={opening.speciality.id}
            specialityKey={opening.speciality.key}
          />
        )}
      </div>

      {isLocationsDisplayed && <Locations opening={opening} setField={setField} />}

      {isLocationsDisplayed && (!isEmpty(opening.positionLocations) || opening.isRemote) ? (
        <div className="page-section">
          <SalaryBudget
            activeSalary={salaryRange}
            employmentType={employmentType}
            openingId={openingId}
            setSalary={fields => setField(fields)}
            updatedAt={updatedAt}
          />
        </div>
      ) : null}

      {isLocationsDisplayed &&
        (!isEmpty(opening.positionLocations) || opening.isRemote) &&
        !isEmpty(opening.targetLocations) && (
          <div className="page-section">
            <Button
              className="create-opening-continue"
              isDisabled={saving || !isSalaryRangeComplete(opening)}
              isLink
              onClick={() =>
                trackEvent({
                  context: MetricContext.CREATE_OPENING,
                  interaction: MetricAction.OPEN_MESSAGE_VIEW,
                  payload: {
                    'Event Source': MetricEventSource.CONTINUE_BUTTON,
                    'Opening Id': openingId,
                    'Previous Context': MetricContext.CREATE_OPENING_TARGETING,
                  },
                })
              }
              params={{ openingId }}
              to={FUNNEL_MESSAGE_ROUTE}
            >
              Continue
            </Button>
          </div>
        )}
    </div>
  );
};

View.propTypes = {
  opening: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  setField: PropTypes.func.isRequired,
  setSeniorities: PropTypes.func.isRequired,
  setSkills: PropTypes.func.isRequired,
  setSpeciality: PropTypes.func.isRequired,
  updatedAt: PropTypes.string,
};
